import {
  Button,
  Col,
  Flex,
  Radio,
  RadioChangeEvent,
  Result,
  Row,
  Spin,
} from 'antd'
import { Typography } from 'antd'
import {
  CartesianGrid,
  ComposedChart,
  Customized,
  Dot,
  Line,
  Rectangle,
  ReferenceArea,
  ResponsiveContainer,
  Scatter,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import {
  ChartDataPointType,
  TooltipPropsType,
  VerticalGeneratorPropsType,
} from './types'
import dayjs, { Dayjs } from 'dayjs'
import { datetimeToAusFormat, getBandedRanges } from './util'
import { recordedDataRadioSizing } from '../RecordedVitalsTab/RecordedVitalsTabStyle'
import { usePatientTabContext } from '../../PatientTabs'
import PrintButton from './PrintButton'
import { useDispatch, useSelector } from 'react-redux'
import {
  selectAddsDataError,
  selectAddsDataLoading,
} from '../../../../state/addsData/addsDataReducer'
import { useEffect } from 'react'
import { SyncOutlined } from '@ant-design/icons'

const { Title, Text } = Typography

export type PatientDetailsType = {
  patientKey: string
  patientName: string
  patientDOB: string | undefined
  patientAge: number | undefined
  doctorName: string | undefined
  hospitalName: string | undefined
}

type AddsChartPropsType = {
  patientDetails: PatientDetailsType
  chartData: ChartDataPointType[]
  timeArray: Dayjs[]
  timeTicks: number[]
  startTime: number
  endTime: number
  isPrintMode: boolean
  sync: () => void
  onDurationChange: (event: RadioChangeEvent) => void
  onIntervalChange: (event: RadioChangeEvent) => void
  printToggle: () => void
}

type CustomTickProps = {
  x?: number
  y?: number
  payload?: {
    value: number
  }
}

type CustomizedAxisTickPropsType = {
  x?: number
  y?: number
  stroke?: string
  payload?: {
    value: number
  }
  labels: string[]
}

type ChartsType = {
  height: number | string
  title: JSX.Element
  description: JSX.Element
  labelDomain: [number, number]
  dataDomain: [number, number]
  ticks: number[]
  labels: string[]
  dataKey: string
  bandLabel:
    | 'RESPIRATORY_RATE'
    | 'O2_SATURATION'
    | 'PULSE_RATE'
    | 'TEMPERATURE'
    | 'URINE'
    | 'CONSCIOUSNESS'
    | 'PAIN'
    | null
}

const chartRowStyle = {
  border: '0.5px solid #323232',
}

const chartColStyle = {
  borderLeft: '0.5px solid #323232',
  padding: '0 0 0 0.65% ',
}

const patientDetailsStyle = {
  fontSize: '1.1em',
}

const chartControlTitleStyle = {
  fontSize: '1em',
}

const chartTitleStyle = {
  fontSize: '1.3em',
}

const chartDescStyle = {
  fontSize: '0.9em',
}

const tooltipStyle = {
  width: '170px',
  padding: '7px',
  borderRadius: '5px',
  background: '#ffffff',
  opacity: 0.9,
  fontSize: '0.8em',
}

const charts: ChartsType[] = [
  {
    height: '12.59%',
    title: (
      <Title level={4} style={chartTitleStyle}>
        Respiratory Rate
      </Title>
    ),
    description: <Text style={chartDescStyle}>(breaths / min)</Text>,
    labelDomain: [0, 7],
    dataDomain: [1, 8],
    ticks: [0, 1, 2, 3, 4, 5, 6],
    labels: ['≤ 4', '5-8', '9-20', '21-30', '31-35', '36', '≥ 37'],
    dataKey: 'resp',
    bandLabel: 'RESPIRATORY_RATE',
  },
  {
    height: '7.56%',
    title: (
      <Title level={4} style={chartTitleStyle}>
        O<sub>2</sub> Saturation
      </Title>
    ),
    description: <Text style={chartDescStyle}>(%)</Text>,
    labelDomain: [0, 4],
    dataDomain: [1, 5],
    ticks: [0, 1, 2, 3],
    labels: ['≤ 84', '85-89', '90-92', '≥ 93'],
    dataKey: 'spo2',
    bandLabel: 'O2_SATURATION',
  },
  {
    height: '20.15%',
    title: (
      <Title level={4} style={chartTitleStyle}>
        Blood Pressure
      </Title>
    ),
    description: <Text style={chartDescStyle}>(mmHg)</Text>,
    labelDomain: [40, 210],
    dataDomain: [40, 210],
    ticks: [
      50, 60, 70, 80, 90, 100, 110, 120, 130, 140, 150, 160, 170, 180, 190, 200,
    ],
    labels: [],
    dataKey: 'sys',
    bandLabel: null,
  },
  {
    height: '15.11%',
    title: (
      <Title level={4} style={chartTitleStyle}>
        Heart Rate
      </Title>
    ),
    description: <Text style={chartDescStyle}>(beats / min)</Text>,
    labelDomain: [0, 7],
    dataDomain: [1, 8],
    ticks: [0, 1, 2, 3, 4, 5, 6],
    labels: [
      '30-39',
      '40-49',
      '50-99',
      '100-109',
      '110-129',
      '130-139',
      '≥ 140',
    ],
    dataKey: 'pr',
    bandLabel: 'PULSE_RATE',
  },
  {
    height: '10.08%',
    title: (
      <Title level={4} style={chartTitleStyle}>
        Temperature
      </Title>
    ),
    description: <Text style={chartDescStyle}>(&deg;C)</Text>,
    labelDomain: [0, 6],
    dataDomain: [1, 7],
    ticks: [0, 1, 2, 3, 4, 5],
    labels: ['≤ 34', '34.1-35', '35.1-36', '36.1-37.9', '38-38.5', '≥ 38.6'],
    dataKey: 'temp',
    bandLabel: 'TEMPERATURE',
  },
  {
    height: '10.08%',
    title: (
      <Title level={4} style={chartTitleStyle}>
        4 Hour Urine Output
      </Title>
    ),
    description: <Text style={chartDescStyle}>(mL)</Text>,
    labelDomain: [0, 4],
    dataDomain: [1, 5],
    ticks: [0, 1, 2, 3],
    labels: ['<= 79', '80-119', '120-799', '>= 800'],
    dataKey: 'urine',
    bandLabel: 'URINE',
  },
  {
    height: '10.08%',
    title: (
      <Title level={4} style={chartTitleStyle}>
        Consciousness
      </Title>
    ),
    description: (
      <Text style={chartDescStyle}>
        If necessary, wake patient before screening
      </Text>
    ),
    labelDomain: [0, 4],
    dataDomain: [1, 5],
    ticks: [0, 1, 2, 3],
    labels: ['Unresp.', 'Pain', 'Voice', 'Alert'],
    dataKey: 'consc',
    bandLabel: 'CONSCIOUSNESS',
  },
  {
    height: '10.08%',
    title: (
      <Title level={4} style={chartTitleStyle}>
        Pain
      </Title>
    ),
    description: <Text style={chartDescStyle}></Text>,
    labelDomain: [0, 4],
    dataDomain: [1, 5],
    ticks: [0, 1, 2, 3],
    labels: ['0', '1-3', '4-6', '7-10'],
    dataKey: 'pain',
    bandLabel: 'PAIN',
  },
]

const AddsChart = ({
  patientDetails,
  chartData,
  timeArray,
  timeTicks,
  startTime,
  endTime,
  isPrintMode = false,
  sync,
  onDurationChange,
  onIntervalChange,
  printToggle,
}: AddsChartPropsType) => {
  const { activeKey, size } = usePatientTabContext()
  const isLoading = useSelector(selectAddsDataLoading)
  const error = useSelector(selectAddsDataError)

  const CustomXAxisTick: React.FC<CustomTickProps> = ({ x, y, payload }) => {
    if (!payload?.value) return <g></g>
    const date = dayjs.unix(payload.value)
    return (
      <g>
        <text x={x} y={isPrintMode ? y! - 40 : y! - 35} fontSize='0.9em'>
          <tspan textAnchor='middle' dy={isPrintMode ? 15 : 0}>
            {date.format('MM/DD')}
          </tspan>
          <tspan textAnchor='middle' x={x} dy={isPrintMode ? 12 : 15}>
            {date.format('HH:mm')}
          </tspan>
        </text>
      </g>
    )
  }

  const CustomizedAxisTick: React.FC<CustomizedAxisTickPropsType> = ({
    x = 0,
    y = 0,
    stroke,
    payload,
    labels,
  }) => {
    if (!payload) return null

    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={isPrintMode ? 3 : -3}
          dy={-10}
          textAnchor='end'
          fill='#000000'
          fontStyle='bold'
          fontSize='0.85em'
        >
          {labels[payload.value]}
        </text>
      </g>
    )
  }

  const customVerticalGridLines = (props: VerticalGeneratorPropsType) => {
    const numPoints = timeArray.length > 49 ? 48 : timeArray.length - 1
    const adjustedWidth = props.width - (props.offset.left + props.offset.right)
    const spacing = adjustedWidth / numPoints
    return Array.from(
      { length: numPoints - 1 },
      (_, i) => props.offset.left + spacing * (i + 1)
    )
  }

  const CustomTooltip: React.FC<TooltipPropsType> = ({ active, payload }) => {
    if (!active || !payload || payload.length === 0) {
      return null
    }

    let name: JSX.Element = <p></p>
    let value = payload[0].value
    const actualvalues = payload[0].payload
    const createTimestamp = payload[0].payload.createTimestamp

    switch (payload[0].name) {
      case 'resp':
        name = <strong>Respiration</strong>
        value = actualvalues.respiration
        break
      case 'spo2':
        name = (
          <strong>
            O<sub>2</sub> Saturation
          </strong>
        )
        value = actualvalues.o2Saturation
        break
      case 'pr':
        name = <strong>Heart Rate</strong>
        value = actualvalues.heartRate
        break
      case 'temp':
        name = <strong>Temperature</strong>
        value = actualvalues.temperature.toFixed(2)
        break
      case 'urine':
        name = <strong>4 Hr Urine Output</strong>
        value = actualvalues.fourHourUrineOutput
        break
      case 'consc':
        name = <strong>Consciousness</strong>
        value = actualvalues.consciousness
        break
      case 'pain':
        name = <strong>Pain</strong>
        value = actualvalues.painValue
        break
    }

    if (payload[0].name === 'sys' || payload[0].name === 'dia') {
      if (!actualvalues.sys) {
        return null
      }
      return (
        <Flex style={tooltipStyle} vertical>
          <p>
            <strong>Systolic</strong> : {actualvalues.sys}
          </p>
          <p>
            <strong>Diastolic</strong> : {actualvalues.dia}
          </p>
          <p>{datetimeToAusFormat(createTimestamp)}</p>
        </Flex>
      )
    } else {
      return (
        <Flex style={tooltipStyle} vertical>
          <p>
            {name} : {value}
          </p>
          <p>{datetimeToAusFormat(createTimestamp)}</p>
        </Flex>
      )
    }
  }

  const CustomizedRectangle = (props: any) => {
    const { formattedGraphicalItems } = props
    // get dia and sys from chart
    const firstSeries = formattedGraphicalItems[0]
    const secondSeries = formattedGraphicalItems[1]

    // render rectangle using scatter points from the graph
    return firstSeries?.props?.points.map(
      (firstSeriesPoint: any, index: any) => {
        const secondSeriesPoint = secondSeries?.props?.points[index]
        const yDifference = firstSeriesPoint.y - secondSeriesPoint.y

        return (
          <g key={`dia-sys${firstSeriesPoint.y}`}>
            <Rectangle
              key={firstSeriesPoint.payload.createTimestamp}
              width={isPrintMode ? 2 : 4}
              height={yDifference}
              x={secondSeriesPoint.x + (isPrintMode ? 3 : 2)}
              y={secondSeriesPoint.y}
              fill={'black'}
            />
            <Dot
              cx={firstSeriesPoint.x + 4}
              cy={firstSeriesPoint.y}
              r={isPrintMode ? 2 : 5}
              fill='black'
            />
            <Dot
              cx={firstSeriesPoint.x + 4}
              cy={secondSeriesPoint.y}
              r={isPrintMode ? 2 : 5}
              fill='black'
            />
          </g>
        )
      }
    )
  }

  return (
    <Flex style={{ padding: '1.1%' }} vertical>
      <Flex className='chart-header' style={{ padding: '0.5% 0' }} vertical>
        <Row className='chart-header-title'>
          <Col span={24}>
            <Flex justify='space-between'>
              <Title>ADDS Chart</Title>
              <Flex>
                <Button
                  icon={<SyncOutlined />}
                  onClick={sync}
                  type='primary'
                  style={{ marginRight: '7px' }}
                ></Button>
                <PrintButton
                  patientDetails={patientDetails}
                  startTime={startTime}
                  endTime={endTime}
                  printToggle={printToggle}
                />
              </Flex>
            </Flex>
          </Col>
        </Row>
        <Row className='chart-header-body'>
          <Col span={14}>
            <Flex justify='space-between'>
              <Flex>
                <Text style={patientDetailsStyle}>Patient Name:&nbsp;</Text>
                <Text style={patientDetailsStyle} strong>
                  {patientDetails.patientName}
                </Text>
              </Flex>
              <Flex>
                <Text style={patientDetailsStyle}>Patient DOB:&nbsp;</Text>
                <Text style={patientDetailsStyle} strong>
                  {patientDetails.patientDOB}
                </Text>
              </Flex>
              <Flex>
                <Text style={patientDetailsStyle}>Patient Age:&nbsp;</Text>
                <Text style={patientDetailsStyle} strong>
                  {patientDetails.patientAge}
                </Text>
              </Flex>
              <Flex>
                <Text style={patientDetailsStyle}>Doctor Name:&nbsp;</Text>
                <Text style={patientDetailsStyle} strong>
                  {patientDetails.doctorName}
                </Text>
              </Flex>
            </Flex>
          </Col>
          <Col span={10}>
            <Flex justify='flex-end'>
              <Text style={patientDetailsStyle} strong>
                {patientDetails.hospitalName}
              </Text>
            </Flex>
          </Col>
        </Row>
      </Flex>
      <Flex className='chart-control' vertical>
        <Flex style={chartRowStyle}>
          <Flex flex={2.5}>
            <Text></Text>
          </Flex>
          <Flex
            flex={21.5}
            style={{ ...chartColStyle, padding: '10px' }}
            vertical
          >
            <Text style={chartControlTitleStyle} strong>
              Time:
            </Text>
            <Radio.Group
              style={{ display: 'flex' }}
              size={size}
              onChange={onDurationChange}
              defaultValue={24}
            >
              {[1, 3, 6, 12, 24].map((item, index) => (
                <Radio.Button
                  style={{ ...recordedDataRadioSizing[size] }}
                  key={index}
                  value={item}
                >
                  {item} {item > 1 ? 'Hours' : 'Hour'}
                </Radio.Button>
              ))}
            </Radio.Group>
            <Text style={chartControlTitleStyle}>Intervals:</Text>
            <Radio.Group
              style={{ display: 'flex', flexWrap: 'wrap' }}
              size={size}
              onChange={onIntervalChange}
              defaultValue={10}
            >
              {[10, 20, 30, 60].map((item, index) => (
                <Radio.Button
                  style={{ ...recordedDataRadioSizing[size] }}
                  key={index}
                  value={item}
                >
                  {item} {item > 1 ? 'Minutes' : 'Minute'}
                </Radio.Button>
              ))}
            </Radio.Group>
          </Flex>
        </Flex>
      </Flex>
      {isLoading ? (
        <Flex justify='center' align='center' style={{ height: '1000px' }}>
          <Spin />
        </Flex>
      ) : error ? (
        <Flex justify='center' align='center' style={{ height: '1000px' }}>
          <Result
            title={`Error: ${error}`}
            extra={
              <Button type='primary' key='retry' onClick={sync}>
                Retry
              </Button>
            }
          />
        </Flex>
      ) : (
        <Flex
          className='chart-body'
          vertical
          style={
            isPrintMode
              ? {
                  height: '260mm',
                  fontSize: '9px',
                  width: '210mm',
                }
              : {
                  height: '1985px',
                }
          }
        >
          <Flex
            className='chart-row'
            style={{ ...chartRowStyle, height: '4.28%' }}
          >
            <Flex
              flex={2.5}
              style={{ textAlign: 'center' }}
              justify='center'
              align='center'
              vertical
            >
              <Title level={4} style={chartTitleStyle}>
                Time
              </Title>
            </Flex>
            <Flex flex={21.5} style={chartColStyle}>
              <ResponsiveContainer key={`chart-container-time-${isPrintMode}`}>
                <ComposedChart
                  data={chartData}
                  margin={{ top: 5, right: 0, bottom: 5, left: 5 }}
                  key={`chart-time-${isPrintMode}`}
                >
                  <XAxis
                    dataKey='createTimestampSeconds'
                    scale='time'
                    type='number'
                    ticks={timeTicks}
                    tick={<CustomXAxisTick />}
                    domain={[timeTicks[0], timeTicks[timeTicks.length - 1]]}
                    height={isPrintMode ? 7 : 30}
                  />
                  <YAxis tickLine={false} tick={false} yAxisId={1} />
                  <YAxis tickLine={false} tick={false} orientation={'right'} />
                </ComposedChart>
              </ResponsiveContainer>
            </Flex>
          </Flex>
          {charts.map((chart) => (
            <Flex
              className='chart-row'
              style={{ ...chartRowStyle, height: chart.height }}
              key={chart.dataKey}
            >
              <Flex
                flex={2.5}
                justify='center'
                align='center'
                style={{ textAlign: 'center' }}
                vertical
              >
                {chart.title}
                {chart.description}
              </Flex>
              <Flex flex={21.5} style={chartColStyle}>
                <ResponsiveContainer
                  key={`chart-container-${chart.dataKey}-${isPrintMode}`}
                >
                  <ComposedChart
                    data={chartData}
                    margin={{ top: 0, right: 0, bottom: 0, left: 5 }}
                    key={`chart-${chart.dataKey}-${isPrintMode}`}
                  >
                    <XAxis
                      dataKey='createTimestampSeconds'
                      scale='time'
                      type='number'
                      ticks={timeTicks}
                      domain={[startTime / 1000, endTime / 1000]}
                      hide
                    />
                    {chart.dataKey != 'sys' && [
                      <YAxis
                        domain={chart.labelDomain}
                        tickLine={false}
                        yAxisId={1}
                        ticks={chart.ticks}
                        tick={<CustomizedAxisTick labels={chart.labels} />}
                        interval={0}
                        type='number'
                      />,
                      <YAxis
                        dataKey={chart.dataKey}
                        domain={chart.dataDomain}
                        tickLine={false}
                        tick={false}
                        orientation={'right'}
                      />,
                      <Scatter
                        type='monotone'
                        dataKey={chart.dataKey}
                        stroke='#000000'
                        isAnimationActive={false}
                        shape={(props: {
                          cx?: 0 | undefined
                          cy?: 0 | undefined
                          payload: any
                        }) => {
                          const { cx = 0, cy = 0, payload } = props
                          return <Dot cx={cx} cy={cy} r={isPrintMode ? 2 : 5} />
                        }}
                      />,
                      <Line
                        type='linear'
                        dataKey={chart.dataKey}
                        stroke='black'
                        strokeWidth={0.5}
                        dot={false}
                        connectNulls={true}
                        isAnimationActive={false}
                      />,
                      getBandedRanges(chart.bandLabel!).map((range) => (
                        <ReferenceArea
                          y1={range.min}
                          y2={range.max}
                          key={range.key}
                          fill={range.color}
                          strokeOpacity={0.3}
                          fillOpacity={0.3}
                        />
                      )),
                    ]}

                    {chart.dataKey === 'sys' && [
                      <YAxis
                        domain={chart.labelDomain}
                        ticks={chart.ticks}
                        interval={0}
                        type='number'
                        allowDataOverflow
                      />,
                      <YAxis
                        domain={chart.dataDomain}
                        tickLine={false}
                        tick={false}
                        orientation={'right'}
                        yAxisId={1}
                        type='number'
                      />,
                      <Scatter
                        dataKey='sys'
                        fill='none'
                        isAnimationActive={false}
                      ></Scatter>,
                      <Scatter
                        dataKey='dia'
                        fill='none'
                        isAnimationActive={false}
                      ></Scatter>,
                      <Customized component={CustomizedRectangle} />,
                    ]}
                    <CartesianGrid
                      verticalCoordinatesGenerator={(props) =>
                        customVerticalGridLines(props)
                      }
                    />
                    <Tooltip content={<CustomTooltip />} />
                  </ComposedChart>
                </ResponsiveContainer>
              </Flex>
            </Flex>
          ))}
        </Flex>
      )}
    </Flex>
  )
}

export default AddsChart
